import { Button, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import * as React from 'react';
import { useContext, useState } from 'react';
import { BulkResponseDialog } from '../common/BulkResponseDialog';
import { AuthContext } from '../_auth/AuthContext';
import { BulkCancelRequest, BulkUtilResponse, RefundType, TerminationSchedule } from '../_types/bulk';
import { ISearchResult } from '../_types/search';
import { ServicePlan } from '../_types/serviceplan';
import { ILocationSummary } from '../_types/user';
import { PlanByLocationSelector } from '../common/Selector/PlanByLocationSelector';
import { CancelMemberships } from '../_service/bulk';
import Icon, { SupportedIcons } from '../common/Icon';
import { BulkShowResultsListButton } from '../common/BulkShowResultsListButton';


interface Props {
  plans: ServicePlan[],
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dateContainer: {
      margin: 10,
      borderRadius: 10,
      boxShadow: '2px 2px 8px rgb(50 50 93 / 20%)',
      padding: theme.spacing(2)
    },
    tooltip: {
      height: 18,
      position: 'absolute',
      cursor: 'pointer'
    }
  })
);

const CancelMembers: React.FC<Props> = (prop: Props) => {
  const { user } = useContext(AuthContext);
  const [location, setLocation] = useState<ILocationSummary | ISearchResult | null>(user.currentLocation || null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [plans, ] = useState<ServicePlan[]>([]);
  const [endOfPeriod, setEndOfPeriod] = useState<boolean>(false);
  const [immediately, setImmediately] = useState<boolean>(false);
  const [refund, setRefund] = useState<RefundType>(RefundType.none);
  const [selectedPlan, setSelectedPlan] = useState<string | null>();
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [ignoreList, setIgnoreList] = useState<string[]>([]);
  const [storedEmailList, setStoredEmailList] = useState<string[] | undefined>();
  const [storedIgnoreList, setStoredIgnoreList] = useState<string[] | undefined>();

  const classes = useStyles();

  const _cancel = async () => {
    if (!location) return alert('Please select a location.');
    if (!endOfPeriod && !immediately) return alert('Please select whether to cancel members immediately or end of period.');
    setIsCancelling(true);

    let request: BulkCancelRequest = {
        service_location_id: location?.id,
        service_plan_id: (selectedPlan && selectedPlan !== 'all plans') ? Number(selectedPlan) : null,
        when: endOfPeriod ? TerminationSchedule.end_of_period : TerminationSchedule.immediately,
        refund_type: refund
    };
    let response: BulkUtilResponse | null;
    
    try {
        response = await CancelMemberships(request);
    } catch (error) {
      setIsCancelling(false);
      return alert('Something went wrong while connecting to the API. Please try again.');
    }

    if (response !== null) {
      setIsCancelling(false);
      setSelectedPlan(null);

      if (response.emails && response.emails.length > 0) {
        setEmailList(response.emails);
        setIsOpen(true);
      }
      
      if (response.ignoreList && response.ignoreList.length > 0) {
        setIgnoreList(response.ignoreList);
        setIsOpen(true);
      }

      if (response.emails && response.emails.length === 0
        && response.ignoreList && response.ignoreList.length === 0) {
          return alert('No memberships were cancelled. Make sure you selected the correct location and/or plans.');
        }
      }
  }

  return (
    <>
      <Grid item>
        <h3>Cancel Members</h3>
        <p>Select a location and a plan to cancel the members of a given plan. You can select "All Plans" to cancel all members at the given location. 
          Check whether you wish to cancel them immediately or at the end of their billing periods. Select a type of refund to provide, if applicable.</p>
      </Grid>
      <PlanByLocationSelector
        onLocationChange={(newValue: ISearchResult | null) => {
          setLocation(newValue);
        }}
        onPlanChange={(event: any) => setSelectedPlan(event.target.value as string)}
        planValue={selectedPlan || ''} 
        planInputLabel='Plans'
        plans={prop.plans}
      />
      <Grid item xs={12} sm={3} className={classes.dateContainer}>
        <FormControlLabel control={<Checkbox onChange={(e, checked) => setEndOfPeriod(!endOfPeriod)} />} disabled={immediately} 
          label={
            <div title="Sets memberships to cancel at end of current billing period. They will still be active until that date.">
              Cancel EOP <Icon className={classes.tooltip} iconName={SupportedIcons.InfoOutlined} color='primary' />
            </div>} />
      </Grid>
      <Grid item xs={12} sm={3} className={classes.dateContainer}>
        <FormControlLabel control={<Checkbox onChange={(e, checked) => setImmediately(!immediately)} />} disabled={endOfPeriod} 
        label={
          <div title="Effectively cancels and expires memberships immediately so that they are no longer active.">
            Cancel Now <Icon className={classes.tooltip} iconName={SupportedIcons.InfoOutlined} color='primary' />
          </div>} />
      </Grid>
      <Grid item xs={12} sm={3} className={classes.dateContainer}>
        <FormControl fullWidth variant="outlined">
          <InputLabel>Refund</InputLabel>
          <Select value={refund}
            label='Refund'
            onChange={(event: any) => setRefund(event.target.value as RefundType)}>
            <MenuItem value={RefundType.none}>None</MenuItem>
            <MenuItem value={RefundType.prorated}>Prorated</MenuItem>
            <MenuItem value={RefundType.full}>Full</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {isCancelling ? <p>Cancelling, please wait... This can take several minutes...</p> :
        <>
          <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-end", height: "2.85em", flexDirection: "column" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => { _cancel() }}>
              Submit
            </Button>
          </Grid>
          <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-start", flexDirection: "column" }}>
            <BulkShowResultsListButton
              title={"Successfully Canceled " + (storedEmailList?.length || 0).toString() + " Member Emails"}
              emailList={storedEmailList}
              ignoreList={storedIgnoreList}
              hide={storedEmailList === undefined}
              csvName={"CancelMembers" + new Date().toLocaleDateString()}
            />
          </Grid>
        </>
      }
      {(emailList.length > 0 || ignoreList.length > 0) && <BulkResponseDialog
        title="Successfully cancelled Member Emails"
        isOpen={isOpen}
        emailList={emailList}
        ignoreList={ignoreList}
        handleClose={() => {
          setIsOpen(false);
          setStoredEmailList(emailList);
          setStoredIgnoreList(ignoreList);
          setEmailList([]);
          setIgnoreList([]);
        }} />}
    </>
  );
}

export default CancelMembers;